import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { User, CreateUserPayload, CreateUserResponse, UserListResponse, Institution, InstitutionListResponse, UserSearchCriteria, Department } from './user.model';
import { environment } from '../../../environments/environment';

const sortByName = <T extends { name?: string | null }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, {
      sensitivity: 'base',
    }),
  );

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly runtimeApiUrl = (window as { __APP_CONFIG__?: { apiUrl?: string } }).__APP_CONFIG__?.apiUrl;
  private readonly baseApiUrl = this.runtimeApiUrl || environment.apiUrl;
  private readonly apiUrl = `${this.baseApiUrl}/users`;
  private readonly departmentApiUrl = `${this.baseApiUrl}/departments`;

  constructor(private http: HttpClient) { }

  getUsers(criteria?: UserSearchCriteria): Observable<User[]> {
    let params = new HttpParams();

    if (criteria) {
      if (criteria.firstName) params = params.set('firstName', criteria.firstName);
      if (criteria.lastName) params = params.set('lastName', criteria.lastName);
      if (criteria.emailId) params = params.set('email', criteria.emailId);
      if (criteria.department) params = params.set('department', criteria.department);
      if (criteria.institution) params = params.set('institution', criteria.institution);
      if (criteria.active) params = params.set('status', criteria.active === 'Yes' ? 'Active' : 'Inactive');
      if (criteria.superAdmin) params = params.set('superAdmin', criteria.superAdmin === 'Yes' ? 'true' : 'false');
    }

    return this.http.get<UserListResponse>(this.apiUrl, { params }).pipe(
      map(response => response.users.map(u => this.mapToUser(u)))
    );
  }

  getInstitutions(): Observable<Institution[]> {
    const allInstitutions: Institution[] = [];
    return this.getDepartments().pipe(
      map(departments => {
        departments.forEach(dept => {
          dept.institutions.forEach(inst => {
            if (!allInstitutions.find(i => i.id === inst.id)) {
              allInstitutions.push(inst);
            }
          });
        });
        return sortByName(allInstitutions);
      })
    );
  }

  getDepartments(): Observable<Department[]> {
    return this.http.get<any>(this.departmentApiUrl).pipe(
      map(response => {
        let deptList: any[] = [];
        if (Array.isArray(response)) {
          deptList = response;
        } else if (response && Array.isArray(response.departments)) {
          deptList = response.departments;
        } else if (response && Array.isArray(response.return_body)) {
          deptList = response.return_body;
        }
        return sortByName(deptList).map((dept, index) => ({
          id: String(dept.pk || dept.id || index),
          name: dept.name || dept.departmentName || dept.displayName,
          institutions: sortByName(dept.institutions || []).map((inst: any, i: number) => ({
            id: String(inst.pk || inst.id || i),
            name: inst.name || inst.institutionName || inst.displayName,
          })),
        }));
      })
    );
  }

  createUser(user: User): Observable<CreateUserResponse> {
    const payload = this.mapToCreatePayload(user);
    return this.http.post<CreateUserResponse>(this.apiUrl, payload);
  }

  getUserByEmail(email: string): Observable<User> {
    const encodedEmail = encodeURIComponent(email);
    return this.http.get<CreateUserResponse>(`${this.apiUrl}/${encodedEmail}`).pipe(
      map(u => this.mapToUser(u))
    );
  }

  updateUser(userEmail: string, user: User): Observable<CreateUserResponse> {
    const fullPayload = this.mapToCreatePayload(user);
    // Remove email from payload since it's the user identifier in the URL path
    const { email, ...payload } = fullPayload;
    const encodedEmail = encodeURIComponent(userEmail);
    return this.http.put<CreateUserResponse>(`${this.apiUrl}/${encodedEmail}`, payload);
  }

  deleteUser(email: string): Observable<void> {
    const encodedEmail = encodeURIComponent(email);
    return this.http.delete<void>(`${this.apiUrl}/${encodedEmail}`);
  }

  private mapToCreatePayload(user: User): CreateUserPayload {
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.emailId,
      department: user.superAdmin ? null : (user.department?.id || null),
      status: user.active === 'Yes' ? 'Active' : 'Inactive',
      superAdmin: user.superAdmin,
      allowAllAccess: user.allowAllAccess,
      ssoId: user.loginSsoId || null,
      userType: user.userType === 'Internal' ? 'internal' : 'external',
      institutions: user.superAdmin ? [] : user.assignedInstitutions.map(i => ({ id: String(i.id), name: i.name })),
      role: user.superAdmin ? null : user.role,
    };
  }

  private mapToUser(apiUser: any): User {
    const firstName = apiUser.firstName || apiUser.name?.split(' ')[0] || '';
    const lastName = apiUser.lastName || apiUser.name?.split(' ').slice(1).join(' ') || '';
    const name = (apiUser.firstName && apiUser.lastName ? `${apiUser.firstName} ${apiUser.lastName}`.trim() : apiUser.name) || '';

    return {
      id: apiUser.id,
      firstName: firstName,
      lastName: lastName,
      name: name,
      emailId: apiUser.email || '',
      active: apiUser.status === 'Active' ? 'Yes' : 'No',
      superAdmin: !!apiUser.superAdmin,
      allowAllAccess: apiUser.allowAllAccess,
      loginSsoId: apiUser.ssoId || '',
      userType: apiUser.userType === 'internal' ? 'Internal' : 'External',
      department: Array.isArray(apiUser.department) && apiUser.department.length > 0
        ? { id: String(typeof apiUser.department[0] === 'string' ? apiUser.department[0] : apiUser.department[0].id || apiUser.department[0].pk), name: apiUser.department[0].name || '' }
        : (apiUser.department && !Array.isArray(apiUser.department)
          ? (typeof apiUser.department === 'string'
            ? { id: apiUser.department, name: '' }
            : { id: String(apiUser.department.id || apiUser.department.pk), name: apiUser.department.name || '' })
          : null),
      assignedInstitutions: (apiUser.institutions || []).map((i: any) =>
        typeof i === 'string' ? { id: i, name: '' } : { id: String(i.id || i.pk), name: i.name || '' }
      ),
      role: apiUser.role && typeof apiUser.role === 'object'
        ? { id: String(apiUser.role.id), name: apiUser.role.name || apiUser.role.roleName }
        : null,
    };
  }
}